import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

// A Mermaid diagram is stored as its source; the browser draws it.
//
// The library is loaded the first time a reader opens one - `import()`, so
// webpack keeps mermaid in a chunk of its own and the main bundle carries
// none of it (the same treatment swagger-ui gets on the Developers page).
//
// Imported source is a stranger's text, so mermaid runs with
// securityLevel "strict": no click handlers, no scripts, and labels drawn as
// SVG text rather than HTML. The source itself is never put into the page -
// only the SVG mermaid produces, which it sanitises under that setting.
// A diagram mermaid cannot parse is a message with the parser's own
// complaint, never a blank pane and never a thrown error.

const MIN = 0.1;
const MAX = 8;

let loading = null;

export const loadMermaid = () => {
  if (!loading) {
    loading = import("mermaid")
      .then(({ default: mermaid }) => {
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          htmlLabels: false,
          flowchart: { htmlLabels: false },
          class: { htmlLabels: false },
          fontFamily: "inherit",
        });
        return mermaid;
      })
      .catch((err) => {
        // Let the next reader try again rather than remembering the failure.
        loading = null;
        throw err;
      });
  }
  return loading;
};

// What mermaid says went wrong, in the reader's words where it has none.
const complaint = (err) => {
  const text = (err && (err.str || err.message)) || "";
  return String(text).trim() || "The diagram couldn't be read.";
};

const drawingId = () => `bf-mermaid-${Math.random().toString(36).slice(2, 10)}`;

const MermaidCanvas = forwardRef(function MermaidCanvas({ source, onError }, ref) {
  const boxRef = useRef(null);
  const docRef = useRef(null);
  const drag = useRef(null);
  const [drawing, setDrawing] = useState({ loading: true, svg: "", error: "" });
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });

  const report = useRef(onError);
  report.current = onError;

  useEffect(() => {
    let active = true;
    setDrawing({ loading: true, svg: "", error: "" });
    setView({ x: 0, y: 0, k: 1 });
    loadMermaid()
      .then((mermaid) => mermaid.render(drawingId(), String(source || "")))
      .then(({ svg }) => {
        if (!active) return;
        setDrawing({ loading: false, svg, error: "" });
      })
      .catch((err) => {
        if (!active) return;
        const message = complaint(err);
        setDrawing({ loading: false, svg: "", error: message });
        report.current?.(message);
      });
    return () => {
      active = false;
    };
  }, [source]);

  // What the drawing covers: its viewBox, else what the browser laid out.
  const naturalSize = useCallback(() => {
    const svg = docRef.current?.querySelector("svg");
    if (!svg) return null;
    const box = (svg.getAttribute("viewBox") || "").trim().split(/[\s,]+/).map(Number);
    if (box.length === 4 && box.every(Number.isFinite) && box[2] > 0 && box[3] > 0) return { width: box[2], height: box[3] };
    const rect = svg.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? { width: rect.width, height: rect.height } : null;
  }, []);

  const fit = useCallback(() => {
    const box = boxRef.current;
    const size = naturalSize();
    if (!box || !size || !box.clientWidth) return;
    const pad = 24;
    const k = Math.min((box.clientWidth - pad * 2) / size.width, (box.clientHeight - pad * 2) / size.height, 2);
    setView({ k, x: (box.clientWidth - size.width * k) / 2, y: (box.clientHeight - size.height * k) / 2 });
  }, [naturalSize]);

  // mermaid sizes its SVG to whatever container it drew in; here it is drawn
  // at its own size and this canvas does the scaling, as the image one does.
  useEffect(() => {
    const svg = docRef.current?.querySelector("svg");
    if (!svg) return;
    const size = naturalSize();
    svg.style.maxWidth = "none";
    if (size) {
      svg.setAttribute("width", `${size.width}`);
      svg.setAttribute("height", `${size.height}`);
      svg.style.width = `${size.width}px`;
      svg.style.height = `${size.height}px`;
    }
    fit();
  }, [drawing.svg, fit, naturalSize]);

  const zoomAt = useCallback((factor, cx, cy) => {
    setView((v) => {
      const k = Math.min(MAX, Math.max(MIN, v.k * factor));
      const ratio = k / v.k;
      return { k, x: cx - (cx - v.x) * ratio, y: cy - (cy - v.y) * ratio };
    });
  }, []);

  const zoomCentre = (factor) => {
    const box = boxRef.current;
    if (box) zoomAt(factor, box.clientWidth / 2, box.clientHeight / 2);
  };

  useImperativeHandle(ref, () => ({ zoomIn: () => zoomCentre(1.25), zoomOut: () => zoomCentre(0.8), fit }));

  if (drawing.error) {
    return (
      <div className="bf-mermaid-error" role="alert">
        <p className="bf-mermaid-error__title">This Mermaid diagram couldn&apos;t be drawn</p>
        <p className="bf-mermaid-error__text">The source has to be corrected where it is maintained. Mermaid says:</p>
        {/* Text, never markup: the parser quotes the source it stumbled on. */}
        <pre className="bf-mermaid-error__detail">{drawing.error}</pre>
      </div>
    );
  }

  return (
    <div
      ref={boxRef}
      className="bf-mermaid-canvas"
      onWheel={(e) => {
        const rect = boxRef.current.getBoundingClientRect();
        zoomAt(e.deltaY < 0 ? 1.1 : 0.9, e.clientX - rect.left, e.clientY - rect.top);
      }}
      onPointerDown={(e) => {
        drag.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!drag.current) return;
        const dx = e.clientX - drag.current.x;
        const dy = e.clientY - drag.current.y;
        drag.current = { x: e.clientX, y: e.clientY };
        setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
      }}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
    >
      <div
        ref={docRef}
        className="bf-mermaid-canvas__doc"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}
        // mermaid's own output, sanitised by it under securityLevel "strict".
        dangerouslySetInnerHTML={{ __html: drawing.svg }}
      />
    </div>
  );
});

export default MermaidCanvas;
